"use client";

import { memo, type ReactNode, useMemo } from "react";
import { cn } from "../../lib/utils";
import { MobileAdapter } from "./adapters/mobile";
import { WebAdapter } from "./adapters/web";
import { defaultAvatarRenderer, defaultOverflowRenderer } from "./renderers";
import type { BaseInteraction, FacepileCoreProps, FacepileSize } from "./types";

/**
 * Core facepile component that handles all common logic
 * - Item processing (filter, sort)
 * - Layout and positioning
 * - Overflow calculation
 * - Event handling
 * - Platform-specific rendering via adapters
 */
export const FacepileCore = memo(
	<T extends BaseInteraction>({
		items,
		maxVisible = 5,
		size = "sm",
		className,
		renderAvatar = defaultAvatarRenderer,
		renderInteraction,
		renderOverflow = defaultOverflowRenderer,
		sortStrategy,
		filterStrategy,
		context,
		onItemClick,
		onOverflowClick,
		enableHoverCards = true,
		enableLinks = true,
		modalAware = false,
		platform = "web",
	}: FacepileCoreProps<T>) => {
		// Process items through filter and sort strategies
		const processedItems = useMemo(() => {
			let processed = [...items];

			// Apply filter strategy if provided
			if (filterStrategy) {
				processed = filterStrategy(processed);
			}

			// Apply sort strategy if provided
			if (sortStrategy) {
				processed = sortStrategy(processed);
			}

			return processed;
		}, [items, filterStrategy, sortStrategy]);

		// Calculate visible and hidden items
		const { visibleItems, hiddenCount } = useMemo(() => {
			const visible = processedItems.slice(0, maxVisible);
			const hidden = Math.max(0, processedItems.length - maxVisible);

			return {
				visibleItems: visible,
				hiddenCount: hidden,
			};
		}, [processedItems, maxVisible]);

		// Don't render if no items
		if (!processedItems || processedItems.length === 0) {
			return null;
		}

		// Base container styles - elegant and bulletproof
		const containerClasses = cn("inline-flex items-center leading-none", className);

		// Platform-specific avatar rendering
		const renderPlatformAvatar = (item: T): ReactNode => {
			if (platform === "mobile") {
				return (
					<MobileAdapter
						item={item}
						size={size}
						renderAvatar={renderAvatar}
						renderInteraction={renderInteraction}
						onItemPress={onItemClick}
					/>
				);
			}

			// Default to web
			return (
				<WebAdapter
					item={item}
					size={size}
					renderAvatar={renderAvatar}
					renderInteraction={renderInteraction}
					href={context?.getHref?.(item)}
					enableHoverCard={enableHoverCards}
					enableLink={enableLinks}
					modalAware={modalAware}
					onItemClick={onItemClick}
				/>
			);
		};

		return (
			<div
				className={containerClasses}
				onMouseDown={(e) => e.stopPropagation()}
				onMouseUp={(e) => e.stopPropagation()}
			>
				{/* Render visible avatars with elegant overlap */}
				{visibleItems.map((item, index) => (
					<div
						key={item.id}
						className="relative inline-flex leading-none"
						style={{
							marginLeft: index === 0 ? 0 : "-0.5rem",
							zIndex: index + 1,
						}}
					>
						{renderPlatformAvatar(item)}
					</div>
				))}

				{/* Render overflow indicator */}
				{hiddenCount > 0 && (
					<div
						className="relative inline-flex leading-none"
						style={{
							marginLeft: "-0.5rem",
							zIndex: visibleItems.length + 1,
						}}
						onClick={onOverflowClick}
					>
						{renderOverflow(hiddenCount, size)}
					</div>
				)}
			</div>
		);
	},
);

FacepileCore.displayName = "FacepileCore";
